import AlternateColors from '@repo/ui/src/components/AlternateColors';
import { useProductVariations } from '@hooks/useProductVariations';
import Product from '@models/product';
import { StateSetterFunction } from '@repo/ui/src/types/helpers';
import { ProductVariation, VariationGroup } from '@typings/product';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';

export interface TierThreeVariationsProps {
  product: Product;
  activeVariation: ProductVariation | undefined;
  setActiveVariation: StateSetterFunction<ProductVariation | undefined>;
}

const TierThreeVariations = ({
  product,
  activeVariation,
  setActiveVariation,
}: TierThreeVariationsProps) => {
  const { t } = useTranslation();

  const [color, size] = useProductVariations(product, [
    VariationGroup.apparelColor,
    VariationGroup.apparelSize,
  ]);

  return (
    <>
      {size.variations && size.variations.length > 0 && (
        <>
          <ul className="hidden md:flex list-none px-0 gap-2 flex-wrap">
            {size.variations.map((variation) => {
              const value = variation.attributesMap[VariationGroup.apparelSize];
              return (
                <li key={value}>
                  <button
                    onClick={() => setActiveVariation(variation!)}
                    className={classNames(
                      'w-12 aspect-square bg-white grid place-content-center tra-text-web-utl-button-m transition-all relative',
                      variation!.id === activeVariation?.id
                        ? 'border-4 border-brand-400 text-brand-300'
                        : 'border-2 border-neutral-300 disabled:border-0 disabled:text-warm-400',
                    )}
                    disabled={!variation!.isOrderable}
                    aria-label={`${value} ${!variation!.isOrderable && 'Out of Stock'}`}
                  >
                    {value}
                    {variation!.id !== activeVariation?.id && !variation!.isOrderable && (
                      <span className="absolute inset-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 49 49" fill="none">
                          <path stroke="currentColor" strokeWidth="2" d="M1.5 2h46v46h-46z" />
                          <path
                            stroke="currentColor"
                            strokeLinecap="square"
                            strokeWidth="2"
                            d="M1.5 47.448 46.898 2"
                          />
                        </svg>
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="md:hidden grid gap-y-2">
            <h4 className="tra-text-web-utl-link">
              {t('productDetailPageContent.tierThreeSelectLabel', undefined, {
                default: 'Size',
              })}
            </h4>
            <div className="relative grid after:block after:absolute after:bg-dropdown after:bg-cover after:inset-y-4 after:right-4 after:w-6 after:pointer-events-none">
              <select
                onChange={(e) =>
                  setActiveVariation(
                    size.variations?.find(
                      (variation) =>
                        variation.attributesMap[VariationGroup.apparelSize] === e.target.value,
                    ),
                  )
                }
                className="bg-white border-2 border-neutral-300 px-4 py-3 w-full"
              >
                <option value="default" key="default" selected disabled hidden>
                  -
                </option>
                {size.variations.map((variation) => (
                  <option
                    key={variation!.id}
                    value={variation.attributesMap[VariationGroup.apparelSize]}
                    selected={variation!.id === activeVariation?.id}
                    disabled={!variation!.isOrderable}
                  >
                    {variation.attributesMap[VariationGroup.apparelSize]}
                    {!variation!.isOrderable && ' - Out of Stock'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}
      {color.variations && color.variations.length > 1 && color.active && (
        <AlternateColors
          activeColor={color.active}
          colors={color.variations.map((c) => ({
            label: c.attributesMap[VariationGroup.apparelColor],
            imageUrl: c.colorVariationImage,
            isAvailable: c.isOrderable,
            href: c.productUrl,
          }))}
        />
      )}
    </>
  );
};

export default TierThreeVariations;
